import React, { useState, useEffect } from 'react';
import MoleculeImage from './MoleculeImage';
import StructureEditor from './StructureEditor';
import ReactionCard from './ReactionCard';
import API_BASE from '../config';
import './Search.css';

const Search = () => {
  const [mode, setMode] = useState('text'); // 'text' | 'substructure'

  // Text search state
  const [textQuery, setTextQuery] = useState('');
  const [textType, setTextType] = useState('all');
  const [reactionTypes, setReactionTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/reaction-types`)
      .then(r => r.json())
      .then(data => setReactionTypes(data.filter(t => t.reaction_type)))
      .catch(() => {});
  }, []);

  // Substructure search state
  const [querySmiles, setQuerySmiles] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');

  // Shared state
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const switchMode = (newMode) => {
    setMode(newMode);
    setResults([]);
    setSearched(false);
    setError(null);
    setTypeFilter('');
  };

  const handleTextSearch = async (e) => {
    e.preventDefault();
    if (textType !== 'unassigned' && !textQuery.trim() && !typeFilter) { setError('Please enter a search term or select a reaction type'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      const typeParam = typeFilter ? `&type=${encodeURIComponent(typeFilter)}` : '';
      const resp = await fetch(
        `${API_BASE}/reactions?search=${encodeURIComponent(textQuery)}&field=${textType}&per_page=1000${typeParam}`
      );
      const data = await resp.json();
      setResults(data.reactions || []);
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubstructureSearch = async () => {
    if (!querySmiles) { setError('Please draw a structure first'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      const resp = await fetch(`${API_BASE}/search/substructure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smiles: querySmiles, role: roleFilter }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Search failed');
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditorSave = (smiles) => {
    setQuerySmiles(smiles);
    setShowEditor(false);
  };

  const handleClear = () => {
    setResults([]); setSearched(false); setError(null);
    setTextQuery(''); setQuerySmiles(''); setTypeFilter('');
  };

  return (
    <div className="search-container">
      <div className="search-header">
        <h1>Search Reactions</h1>
      </div>

      {/* Mode toggle */}
      <div className="search-mode-toggle">
        <button
          className={`mode-btn ${mode === 'text' ? 'active' : ''}`}
          onClick={() => switchMode('text')}
        >
          Text Search
        </button>
        <button
          className={`mode-btn ${mode === 'substructure' ? 'active' : ''}`}
          onClick={() => switchMode('substructure')}
        >
          Substructure Search
        </button>
      </div>

      {/* Text search form */}
      {mode === 'text' && (
        <form onSubmit={handleTextSearch} className="search-form">
          <div className="search-inputs">
            {textType !== 'unassigned' && (
              <input
                type="text"
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                placeholder={textType === 'record_number' ? 'e.g. 316' : "e.g. 'coupling', 'palladium'..."}
                className="search-input"
              />
            )}
            <select value={textType} onChange={(e) => setTextType(e.target.value)} className="search-type-select">
              <option value="all">All Fields</option>
              <option value="reaction_type">Reaction Type</option>
              <option value="reagents">Reagents</option>
              <option value="url">Reference / URL</option>
              <option value="record_number">Reaction #</option>
              <option value="unassigned">No Reaction Type</option>
            </select>
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            {searched && <button type="button" onClick={handleClear} className="clear-button">Clear</button>}
          </div>
          <div className="type-filter-row">
            <label className="type-filter-label">Filter by reaction type:</label>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="type-filter-select"
            >
              <option value="">All types</option>
              {reactionTypes.map(t => (
                <option key={t.reaction_type} value={t.reaction_type}>
                  {t.reaction_type} ({t.count})
                </option>
              ))}
            </select>
          </div>
        </form>
      )}

      {/* Substructure search form */}
      {mode === 'substructure' && (
        <div className="search-form">
          <div className="substructure-controls">
            <div className="draw-area">
              <button className="draw-button" onClick={() => setShowEditor(true)}>
                {querySmiles ? 'Redraw Structure' : 'Draw Structure'}
              </button>
              {querySmiles && (
                <div className="query-preview">
                  <MoleculeImage smiles={querySmiles} width={160} height={160} />
                  <div className="query-smiles">{querySmiles}</div>
                </div>
              )}
            </div>
            <div className="substructure-options">
              <label>Search in:</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="search-type-select">
                <option value="">All molecules</option>
                <option value="reactant">Reactants only</option>
                <option value="reagent">Reagents only</option>
                <option value="product">Products only</option>
              </select>
              <button
                className="search-button"
                onClick={handleSubstructureSearch}
                disabled={loading || !querySmiles}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
              {searched && <button type="button" onClick={handleClear} className="clear-button">Clear</button>}
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {searched && !loading && (
        <div className="search-results">
          <h2>
            {results.length} reaction{results.length !== 1 ? 's' : ''} found
            {mode === 'text' && textQuery && ` for "${textQuery}"`}
          </h2>
          {results.length > 0 ? (
            <div className="reactions-grid">
              {results.map((reaction) => (
                <ReactionCard key={reaction.id} reaction={reaction} />
              ))}
            </div>
          ) : (
            <div className="no-results">
              <p>No reactions found.</p>
              <p>{mode === 'substructure' ? 'Try a simpler substructure or different role filter.' : 'Try different keywords.'}</p>
            </div>
          )}
        </div>
      )}

      {showEditor && (
        <StructureEditor
          smiles={querySmiles}
          onSave={handleEditorSave}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
};

export default Search;
